/**
 * Frontend entry point.
 *
 * MSAL boot order — this MUST be sequenced or the redirect flow silently
 * drops the auth response:
 *   1. instance.initialize()
 *   2. instance.handleRedirectPromise() — parses the fragment-encoded auth
 *      response that Entra appends when redirecting back to the SPA. If we
 *      render before this resolves, msal-react sees "no account" and the
 *      user is bounced back to the Login screen, perpetually.
 *   3. setActiveAccount() so api.ts can pull it.
 *   4. Render React.
 *
 * Documented here because every team trips on this exactly once.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { msalInstance } from "./lib/msal";
import { App } from "./App";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

async function boot() {
  await msalInstance.initialize();
  const response = await msalInstance.handleRedirectPromise();
  if (response?.account) {
    msalInstance.setActiveAccount(response.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && accounts[0]) msalInstance.setActiveAccount(accounts[0]);
  }
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
}

void boot();
